import React from 'react';

/*
 * Bare domains are matched only against a known list of endings. Without it every "word.word"
 * reads as a domain, and .zip / .mov / .app are real TLDs — file names in a sentence would turn
 * into links. Adding an ending here is the supported way to widen this.
 */
const TLDS = [
  'com', 'org', 'net', 'edu', 'gov', 'info', 'biz', 'io', 'co', 'ai', 'app', 'dev', 'live', 'tv',
  'me', 'us', 'uk', 'ca', 'mx', 'es', 'br', 'ar', 'cl', 'de', 'fr', 'it', 'nl', 'au', 'nz', 'jp'
].join('|');

/*
 * Three shapes: a full http(s) address, a www. host, or a bare domain such as livecontrol.io.
 * Only these become links, which is also what keeps javascript: and data: out of an href built
 * from whatever a viewer typed.
 */
const URL_PATTERN = new RegExp(
  String.raw`((?:https?://|www\.)\S+|[\da-z][\da-z-]*(?:\.[\da-z-]+)*\.(?:${TLDS})\b(?:[#/?]\S*)?)`,
  'gi'
);

/* Punctuation right after an address reads as part of the sentence, not of the address. */
const TRAILING = /[!"'),.:;?\]}»]/;

/* An address glued to the text before it is not one: user@apple.com is a mailbox, not a site. */
const GLUED = /[\d@a-z]$/i;

const countOf = (text: string, character: string): number => text.split(character).length - 1;

/*
 * How much of the match is really the address. The closing paren is the awkward one: it ends the
 * sentence in "(see https://x.com)" but belongs to the link in "/wiki/Foo_(bar)", so it is kept
 * only when the address already opened a paren of its own.
 */
const addressLength = (match: string): number => {
  let end = match.length;

  while (end > 0 && TRAILING.test(match.charAt(end - 1))) {
    end -= 1;
  }

  const address = match.slice(0, end);

  return match.charAt(end) === ')' && countOf(address, '(') > countOf(address, ')') ? end + 1 : end;
};

/*
 * Message text with its links clickable. Deliberately built as React nodes rather than with
 * dangerouslySetInnerHTML: this text comes from anyone in the room, and handing it to the HTML
 * parser would turn every message into a place to inject markup.
 */
export const Linkified = ({ text }: { text: string }): React.ReactElement => {
  const parts = text.split(URL_PATTERN);

  return (
    <>
      {parts.map((part, index) => {
        /* split() with a single capture group alternates: plain text, address, plain text, ... */
        if (index % 2 === 0 || !part || GLUED.test(parts[index - 1] ?? '')) {
          return part;
        }

        const address = part.slice(0, addressLength(part));
        const tail = part.slice(address.length);
        const href = /^https?:\/\//i.test(address) ? address : `https://${address}`;

        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={index}>
            {/* noopener/noreferrer: the new tab must not get a handle on the player's window. */}
            <a href={href} target='_blank' rel='noopener noreferrer'>
              {address}
            </a>
            {tail}
          </React.Fragment>
        );
      })}
    </>
  );
};
